import { yupResolver } from "@hookform/resolvers/yup";
import { LoadingButton } from "@mui/lab";
import { Box, DialogActions, DialogContent, Grid, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { FieldValues, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { VerificationProcessService } from "../../../Component/Api/VerificationAgency/VerificationProcess/VerificationProcessService";
import { AppDatepicker } from "../../../Component/Common/Components/FormValidationInputs/AppDatepicker";
import { AppQuill } from "../../../Component/Common/Components/FormValidationInputs/AppQuill";
import AppSelectList, { IDropDownOptions } from "../../../Component/Common/Components/FormValidationInputs/AppSelectList";
import { MDBox } from "../../../Component/Common/Components/MDBox";
import { MDTypography } from "../../../Component/Common/Components/MDTypography";
import { StatusTypeList } from "../../../Component/Common/Constants/Lists/DropdownListOptions";
import { SkeletonLoading } from "../../../Component/Common/SkeletonLoading/SkeletonLoading";
import { VerificationProcess } from "../../../Component/Model/Dto/VerificationAgency/VerificationProcessTracker";
import { VerificationStatusTrackerScheme } from "./VerificationStatusTrackerValidation";

interface IProps {
  id: number;
  scorecardId: string;
  formData: VerificationProcess.VerificationProcessDto;
  showModal: boolean;
  closeModal: () => void;
}

export const VerificationProcessTrackerEditForm = (props: IProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [verificationProcessTypes, setVerificationProcessTypes] = useState<IDropDownOptions[]>([]);
  const [haveVerificationProcessTypes, setHaveVerificationprocessTypes] = useState<boolean>(false);

  const GetVerificationProcessTypes = async (id: string) => {
    try {
      setIsLoading(true);
      const result = await VerificationProcessService.GetVerificationProcessTypes();

      if (result.data.length > 0) {
        setHaveVerificationprocessTypes(true);
      }

      const mappedData = result.data.map(item => ({
        id: item.id,
        value: item.description
      }));

      const hasVerificationProcessTypeId = mappedData.some(item => item.id === props.formData.verificationProcessTypeId);

      reset({
        verificationProcessTypeId: hasVerificationProcessTypeId
          ? props.formData.verificationProcessTypeId
          : '',
        verificationStatus: props.formData.verificationStatus,
        scheduledDate: props.formData.scheduledDate,
        statusDate: props.formData.statusDate,
        note: props.formData.note
      });
      setVerificationProcessTypes(mappedData);

    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const { control, reset, handleSubmit, formState: { isSubmitting } } = useForm({
    mode: "all",
    resolver: yupResolver(VerificationStatusTrackerScheme),
  });

  const handleDataSubmit = async (data: FieldValues) => {
    try {
      const requestData = {
        id: props.id,
        scorecardId: props.scorecardId,
        verificationProcessTypeId: data.verificationProcessTypeId,
        verificationStatus: data.verificationStatus,
        scheduledDate: data.scheduledDate,
        statusDate: data.statusDate,
        note: data.note,
      } as VerificationProcess.VerificationProcessDto;
      await VerificationProcessService.UpdateVerificationProcess(
        requestData
      );
      toast.success("Changes updated successfully");
    } catch (error: any) {
      console.log(error);
    } finally {
      props.closeModal();
    }
  };

  useEffect(() => {
    GetVerificationProcessTypes(props.scorecardId);
  }, []);

  return (
    <>
      {isLoading && <SkeletonLoading formLoading FieldAmount={5} />}
      {!isLoading &&
        <Box>
          <form
            id="UpdateVerificationProcessForm"
            onSubmit={handleSubmit(handleDataSubmit)}
          >
            <Grid container paddingTop={1} paddingBottom="0px">
              <Grid item xs={3}>
                <MDTypography variant='body' sx={{fontWeight: 500}}>Verification Process</MDTypography>
              </Grid>
              <Grid item xs={5}>
                {haveVerificationProcessTypes ? (
                  <AppSelectList control={control}
                    name="verificationProcessTypeId"
                    label="Status"
                    items={verificationProcessTypes} />
                ) : (
                  <Typography style={{ fontSize: '12px', color: 'red' }}>
                    No verification process types found
                  </Typography>
                )}
              </Grid>
            </Grid>

            <Grid container paddingTop={1} paddingBottom="0px">
              <Grid item xs={3}>
                <MDTypography variant='body' sx={{fontWeight: 500}}>Status</MDTypography>
              </Grid>
              <Grid item xs={5}>
                <AppSelectList control={control}
                  name="verificationStatus"
                  label="Status"
                  items={StatusTypeList} />
              </Grid>
            </Grid>

            <Grid container paddingTop={1} paddingBottom="0px">
              <Grid item xs={3}>
                <MDTypography variant='body' sx={{fontWeight: 500}}>Scheduled Date</MDTypography>
              </Grid>
              <Grid item xs={5}>
                <AppDatepicker
                  control={control}
                  name="scheduledDate"
                  label="Scheduled Date"
                  inputFormat="yyyy/MM/dd"
                  minDate={new Date(1900, 1, 1)}
                />
              </Grid>
            </Grid>

            <Grid container paddingTop={1} paddingBottom='0px'>
              <Grid item xs={3}>
                  <MDTypography variant='body' sx={{fontWeight: 500}}>Status Date</MDTypography>
              </Grid>
              <Grid item xs={5}>
                  <AppDatepicker
                      control={control}
                      name="statusDate"
                      label="Status Date"
                      inputFormat='yyyy/MM/dd'
                      minDate={new Date(1900, 1, 1)} />
              </Grid>
            </Grid>
            <Grid container paddingTop={1} paddingBottom='0px'>
                <Grid item xs={3}>
                    <MDTypography variant='body' sx={{fontWeight: 500}}>Note</MDTypography>
                </Grid>
            </Grid>
            <MDBox mt={1} className="leftAlign" >
                <MDTypography variant='button' fontWeight='regular' style={{ color: '#666666', fontSize: "0.85em" }}>
                    Note (Optional, Max 1000 characters)
                </MDTypography>
                <AppQuill control={control} name="note" placeholder="Write something..." />
            </MDBox>
          </form>
          <DialogActions>
            <LoadingButton
              loading={isSubmitting}
              variant="contained"
              color="primary"
              form="UpdateVerificationProcessForm"
              type="submit"
            >
              Update
            </LoadingButton>
          </DialogActions>
        </Box>
      }
    </>
  );
};
